import * as Dialog from '@radix-ui/react-dialog';
import { Check, X, Loader2, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface TemplateParte {
  id: string;
  nome: string;
  descricao?: string;
  riscos: any[];
  ativo: boolean;
  createdAt: string;
}

interface ModalSelecionarTemplateParteProps {
  aberto: boolean;
  onFechar: () => void;
  apreciacaoId: string;
  projetoId?: string;
  onSucesso?: () => void;
}

export function ModalSelecionarTemplateParte({
  aberto,
  onFechar,
  apreciacaoId,
  projetoId,
  onSucesso,
}: ModalSelecionarTemplateParteProps) {
  const { t, formatarData } = useI18n();
  const [templates, setTemplates] = useState<TemplateParte[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [busca, setBusca] = useState('');
  const [templateSelecionado, setTemplateSelecionado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto && projetoId) {
      carregarTemplates();
    }
  }, [aberto, projetoId]);

  const carregarTemplates = async () => {
    if (!projetoId) return;

    setCarregando(true);
    setErro(null);

    try {
      const response = await apiService.listarTemplatePartes(
        projetoId,
        busca || undefined,
        true // apenas ativos
      );

      if (response.success) {
        setTemplates(response.data || []);
      } else {
        setErro(response.message || t('apreciacaoModais.selecionarTemplateParte.erroCarregar'));
      }
    } catch (error) {
      console.error('Erro ao carregar templates de parte:', error);
      setErro(t('apreciacaoModais.selecionarTemplateParte.erroCarregarPartes'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (aberto && projetoId) {
      const timeoutId = setTimeout(() => {
        carregarTemplates();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [busca]);

  const handleAplicar = async () => {
    if (!templateSelecionado) {
      setErro(t('apreciacaoModais.selecionarTemplateParte.erroSelecione'));
      return;
    }

    setErro(null);
    setAplicando(true);

    try {
      const response = await apiService.aplicarTemplateParte(
        templateSelecionado,
        apreciacaoId
      );

      if (response.success) {
        setTemplateSelecionado(null);
        if (onSucesso) {
          onSucesso();
        }
        onFechar();
      } else {
        setErro(response.message || t('apreciacaoModais.selecionarTemplateParte.erroAplicar'));
      }
    } catch (error) {
      console.error('Erro ao aplicar template de parte:', error);
      setErro(t('apreciacaoModais.selecionarTemplateParte.erroAplicar'));
    } finally {
      setAplicando(false);
    }
  };

  const handleFechar = () => {
    if (!aplicando) {
      setBusca('');
      setTemplateSelecionado(null);
      setErro(null);
      onFechar();
    }
  };

  const getQuantidadeRiscos = (template: TemplateParte) => {
    if (Array.isArray(template.riscos)) {
      return template.riscos.length;
    }
    return 0;
  };

  return (
    <Dialog.Root open={aberto} onOpenChange={handleFechar}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[90vw] max-w-2xl max-h-[80vh] overflow-hidden z-50 flex flex-col">
          <div className="p-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-bold text-gray-900">
                {t('apreciacaoModais.selecionarTemplateParte.titulo')}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  disabled={aplicando}
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder={t('apreciacaoModais.selecionarTemplateParte.buscaPlaceholder')}
                  className="w-full border border-gray-300 rounded-md px-10 py-2 text-sm"
                  disabled={aplicando || carregando}
                />
              </div>
            </div>

            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{erro}</p>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {carregando ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-600">{t('apreciacaoModais.selecionarTemplateParte.carregando')}</span>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">
                  {busca ? t('apreciacaoModais.selecionarTemplateParte.vazioBusca') : t('apreciacaoModais.selecionarTemplateParte.vazioLista')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setTemplateSelecionado(template.id)}
                    disabled={aplicando}
                    className={`w-full text-left p-4 border-2 rounded-md transition-colors ${
                      templateSelecionado === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    } ${aplicando ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{template.nome}</h3>
                          {templateSelecionado === template.id && (
                            <Check className="h-4 w-4 text-blue-600" />
                          )}
                        </div>
                        {template.descricao && (
                          <p className="text-sm text-gray-600 mb-2">{template.descricao}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{t('apreciacaoModais.selecionarTemplateParte.riscos', { total: getQuantidadeRiscos(template) })}</span>
                          <span>
                            {t('apreciacaoModais.selecionarTemplateParte.criadoEm', { data: formatarData(template.createdAt) })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 flex-shrink-0">
            <div className="flex justify-end space-x-3">
              <Dialog.Close asChild>
                <button
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={aplicando}
                >
                  {t('comum.acao.cancelar')}
                </button>
              </Dialog.Close>
              <button
                onClick={handleAplicar}
                disabled={aplicando || !templateSelecionado}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {aplicando ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('apreciacaoModais.selecionarTemplateParte.aplicando')}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t('apreciacaoModais.selecionarTemplateParte.botaoAplicar')}
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}






